import React, { useMemo } from 'react';
import {
  sanitizeCommentHtml,
  preprocessSlackMrkdwnUserMentionsInPlainBody,
  PLAIN_COMMENT_MENTION_SPLIT_RE,
} from '../../utils/richComment';
import {
  HUB_HASH_PLAIN_SPLIT_RE,
  hubHashTagFromPlainToken,
  hubHashToken,
  parseHubHashFromElement,
  type HubHashTag,
} from '../../utils/commentHubHashMentions';
import ProposalStructureMentionText from '../proposals/ProposalStructureMentionText';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import { enrichStructureMentionHtmlTitles } from '../../utils/proposalStructureMentions';

interface RichCommentRendererProps {
  /** Sanitized rich HTML rendition of the comment, when present. */
  bodyHtml?: string;
  /** Plain-text legacy body (with `@FirstName` tokens). Used when `bodyHtml` is missing. */
  body: string;
  className?: string;
  /** Set to `true` for taller paragraphs in the inline-edit and pinned section. */
  enlarged?: boolean;
  /** Navigate when an inline `#board` / `#tl` / `#files` / `#COs` chip is clicked. */
  onHubHashClick?: (tag: HubHashTag) => void;
  structureMentionDealId?: string;
  structureMentions?: StructureRef[];
  structuresByDealId?: Map<string, StructureRef[]>;
}

function hubHashChipLabel(tag: HubHashTag): string {
  return hubHashToken(tag);
}

function renderHubHashChip(tag: HubHashTag, key: string, onHubHashClick?: () => void): React.ReactNode {
  return (
    <button
      key={key}
      type="button"
      className={`tc-hub-hash-mention tc-hub-hash-mention--${tag}${tag === 'board' ? ' tc-board-mention' : ''}`}
      onClick={onHubHashClick}
    >
      {hubHashChipLabel(tag)}
    </button>
  );
}

function isHubHashChipTarget(target: EventTarget | null): HubHashTag | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest(
    '[data-hub-link], [data-board], .rce-board-mention, .rce-hub-hash-mention, .tc-hub-hash-mention, .tc-board-mention',
  );
  if (!el) return null;
  return parseHubHashFromElement(el);
}

/** Render the legacy plain-text body, preserving newlines and styling `@mention` tokens. */
function renderLegacyBody(body: string, onHubHashClick?: (tag: HubHashTag) => void): React.ReactNode {
  const normalized = preprocessSlackMrkdwnUserMentionsInPlainBody(body || '');
  const lines = normalized.split('\n');
  return (
    <>
      {lines.map((line, lineIdx) => {
        const parts = line.split(PLAIN_COMMENT_MENTION_SPLIT_RE);
        return (
          <React.Fragment key={lineIdx}>
            {parts.map((part, i) => {
              if (part.startsWith('@') && part.length > 1) {
                return (
                  <span key={i} className="tc-mention">
                    {part}
                  </span>
                );
              }
              const hashParts = part.split(HUB_HASH_PLAIN_SPLIT_RE);
              if (hashParts.length === 1) {
                return <React.Fragment key={i}>{part}</React.Fragment>;
              }
              return (
                <React.Fragment key={i}>
                  {hashParts.map((chunk, bi) => {
                    const tag = hubHashTagFromPlainToken(chunk);
                    if (tag) {
                      return renderHubHashChip(tag, `${i}-${bi}`, () => onHubHashClick?.(tag));
                    }
                    return <React.Fragment key={`${i}-${bi}`}>{chunk}</React.Fragment>;
                  })}
                </React.Fragment>
              );
            })}
            {lineIdx < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </>
  );
}

const RichCommentRenderer: React.FC<RichCommentRendererProps> = ({
  bodyHtml,
  body,
  className,
  enlarged,
  onHubHashClick,
  structureMentionDealId,
  structureMentions,
  structuresByDealId,
}) => {
  const sanitized = useMemo(() => {
    if (!bodyHtml) return '';
    const clean = sanitizeCommentHtml(bodyHtml);
    return enrichStructureMentionHtmlTitles(clean, {
      defaultDealId: structureMentionDealId,
      defaultStructures: structureMentions,
      structuresByDealId,
    });
  }, [bodyHtml, structureMentionDealId, structureMentions, structuresByDealId]);

  const cls = `tc-rich${enlarged ? ' tc-rich--enlarged' : ''}${className ? ` ${className}` : ''}`;

  const handleClick = (e: React.MouseEvent) => {
    if (!onHubHashClick) return;
    const tag = isHubHashChipTarget(e.target);
    if (!tag) return;
    e.preventDefault();
    onHubHashClick(tag);
  };

  if (sanitized) {
    return (
      <div
        className={cls}
        dangerouslySetInnerHTML={{ __html: sanitized }}
        onClick={onHubHashClick ? handleClick : undefined}
      />
    );
  }

  const plain = body || '';
  const hasStructToken = plain.includes('[[struct:');
  if (hasStructToken) {
    return (
      <div className={cls}>
        {renderLegacyBodyWithoutStructureTokens(
          plain,
          onHubHashClick,
          structureMentionDealId,
          structureMentions,
          structuresByDealId,
        )}
      </div>
    );
  }
  return <div className={cls}>{renderLegacyBody(plain, onHubHashClick)}</div>;
};

/** Legacy plain body with structure tokens rendered as badges (rest uses @ / # rules). */
function renderLegacyBodyWithoutStructureTokens(
  body: string,
  onHubHashClick: ((tag: HubHashTag) => void) | undefined,
  structureMentionDealId: string | undefined,
  structureMentions: StructureRef[] | undefined,
  structuresByDealId: Map<string, StructureRef[]> | undefined,
): React.ReactNode {
  const parts = body.split(/(\[\[struct:[^:\]|]+:[^|\]]+\|[^\]]*\]\])/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('[[struct:')) {
          return (
            <ProposalStructureMentionText
              key={i}
              message={part}
              defaultDealId={structureMentionDealId}
              defaultStructures={structureMentions}
              structuresByDealId={structuresByDealId}
            />
          );
        }
        return (
          <React.Fragment key={i}>
            {renderLegacyBody(part, onHubHashClick)}
          </React.Fragment>
        );
      })}
    </>
  );
}

export default RichCommentRenderer;
